import { useState, useEffect } from "react";

const TETRUS_API_KEY = '791457bcf3d49ee84acd659e73b28a38bf39578a8f8cd065a57d83470bf343b1';

const TETRUS_FIELDS = [
  { value: "", label: "-- Skip this column --" },
  { value: "email", label: "Email", required: true },
  { value: "contact_first_name", label: "Contact First Name" },
  { value: "contact_last_name", label: "Contact Last Name" },
  { value: "contact_title", label: "Contact Title / Job Position" },
  { value: "contact_linkedin_url", label: "Contact LinkedIn URL" },
  { value: "company_name", label: "Company Name" },
  { value: "company_url", label: "Company URL / Website" },
  { value: "company_industry", label: "Company Industry" },
  { value: "company_size", label: "Company Size" },
  { value: "company_city", label: "Company City" },
  { value: "company_state", label: "Company State" },
  { value: "company_country", label: "Company Country" },
  { value: "company_zip", label: "Company Zip Code" },
  { value: "list_id", label: "List / Campaign Name" },
  { value: "work_phone", label: "Work Phone" },
  { value: "sic_code", label: "SIC Code" },
  { value: "naics_code", label: "NAICS Code" },
  { value: "annual_revenue", label: "Annual Revenue" },
];

const AMBIGUOUS_FIELDS = {
  linkedin: { question: "Is this the Contact LinkedIn or Company LinkedIn?", options: ["contact_linkedin_url", "company_url"] },
  url: { question: "Is this the Contact URL or Company URL?", options: ["contact_linkedin_url", "company_url"] },
  city: { question: "Is this the Contact City or Company City?", options: ["company_city"] },
  state: { question: "Is this the Contact State or Company State?", options: ["company_state"] },
  social: { question: "Is this Contact Social or Company Social?", options: ["contact_linkedin_url", "company_url"] },
};

function detectAmbiguity(colName) {
  const lower = colName.toLowerCase();
  for (const [key, val] of Object.entries(AMBIGUOUS_FIELDS)) {
    if (lower.includes(key)) return val;
  }
  return null;
}

function autoMap(colName) {
  const lower = colName.toLowerCase().replace(/[^a-z0-9]/g, '');
  const mappings = {
    firstname: "contact_first_name",
    first: "contact_first_name",
    lastname: "contact_last_name",
    last: "contact_last_name",
    email: "email",
    emailaddress: "email",
    company: "company_name",
    companyname: "company_name",
    organization: "company_name",
    title: "contact_title",
    jobtitle: "contact_title",
    jobposition: "contact_title",
    position: "contact_title",
    industry: "company_industry",
    companyindustry: "company_industry",
    companyurl: "company_url",
    website: "company_url",
    companysize: "company_size",
    employees: "company_size",
    city: "company_city",
    companycity: "company_city",
    state: "company_state",
    companystate: "company_state",
    country: "company_country",
    companycountry: "company_country",
    zip: "company_zip",
    zipcode: "company_zip",
    postal: "company_zip",
    list: "list_id",
    campaign: "list_id",
    workdirectphone: "work_phone",
    workphone: "work_phone",
    phone: "work_phone",
    directphone: "work_phone",
    siccode: "sic_code",
    sic: "sic_code",
    naicscode: "naics_code",
    naics: "naics_code",
    annualrevenue: "annual_revenue",
    revenue: "annual_revenue",
  };
  return mappings[lower] || "";
}

function validateEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function validateLinkedIn(url, fieldType) {
  if (!url) return { valid: true, warning: null };
  if (fieldType === "contact_linkedin_url" && url.includes("linkedin.com/company/")) {
    return { valid: false, warning: "This looks like a Company LinkedIn page, not a Contact profile" };
  }
  if (fieldType === "company_url" && url.includes("linkedin.com/in/")) {
    return { valid: false, warning: "This looks like a Contact LinkedIn profile, not a Company page" };
  }
  return { valid: true, warning: null };
}

export default function ImportTool({ onClose }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [preview, setPreview] = useState([]);
  const [mapping, setMapping] = useState({});
  const [ambiguityQueue, setAmbiguityQueue] = useState([]);
  const [currentAmbiguity, setCurrentAmbiguity] = useState(null);
  const [validation, setValidation] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [listName, setListName] = useState('');
  const [activeTab, setActiveTab] = useState('import');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = () => {
    setHistoryLoading(true);
    fetch('https://tetrus.ai/api/import-history', {
      headers: { 'X-API-Key': TETRUS_API_KEY }
    })
      .then(r => r.json())
      .then(d => { setHistory(d.imports || []); setHistoryLoading(false); })
      .catch(() => setHistoryLoading(false));
  };

  useEffect(() => { loadHistory(); }, []);

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(l => l.trim());
    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    const rows = lines.slice(1, 6).map(line => {
      const vals = line.split(',').map(v => v.trim().replace(/"/g, ''));
      const obj = {};
      headers.forEach((h, i) => obj[h] = vals[i] || '');
      return obj;
    });
    return { headers, rows };
  };

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const { headers, rows } = parseCSV(ev.target.result);
      setHeaders(headers);
      setPreview(rows);
      const initialMapping = {};
      const ambiguous = [];
      headers.forEach(h => {
        const mapped = autoMap(h);
        initialMapping[h] = mapped;
        const amb = detectAmbiguity(h);
        if (amb && !mapped) ambiguous.push({ col: h, ...amb });
      });
      setMapping(initialMapping);
      if (ambiguous.length > 0) {
        setAmbiguityQueue(ambiguous);
        setCurrentAmbiguity(ambiguous[0]);
      }
      setStep(2);
    };
    reader.readAsText(f);
  };

  const resolveAmbiguity = (col, value) => {
    setMapping(prev => ({ ...prev, [col]: value }));
    const remaining = ambiguityQueue.slice(1);
    setAmbiguityQueue(remaining);
    setCurrentAmbiguity(remaining.length > 0 ? remaining[0] : null);
  };

  const validateData = () => {
    let issues = [];
    let warnings = [];
    preview.forEach((row, i) => {
      const emailCol = Object.keys(mapping).find(k => mapping[k] === 'email');
      if (emailCol && row[emailCol] && !validateEmail(row[emailCol])) {
        issues.push(`Row ${i + 1}: Invalid email format — "${row[emailCol]}"`);
      }
      Object.keys(mapping).forEach(col => {
        const field = mapping[col];
        if (field === 'contact_linkedin_url' || field === 'company_url') {
          const check = validateLinkedIn(row[col], field);
          if (check.warning) warnings.push(`Row ${i + 1}, ${col}: ${check.warning}`);
        }
      });
    });
    const hasEmail = Object.values(mapping).includes('email');
    if (!hasEmail) issues.push('Email column is required but not mapped');
    setValidation({ issues, warnings });
    setStep(4);
  };

const downloadSkippedCSV = (rows) => {
    const q = v => '"' + String(v == null ? '' : v).replace(/"/g, '""') + '"';
    const header = ['row_number','email','error_reason','raw_data'].join(',');
    const dataLines = rows.map(r => {
      let email = '';
      try { email = JSON.parse(r.raw_data)?.email || ''; } catch {}
      return [q(r.row_number), q(email), q(r.error_reason), q(r.raw_data)].join(',');
    });
    const csvContent = [header].concat(dataLines).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'skipped-rows.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

const handleImport = async () => {
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mapping', JSON.stringify(mapping));
      formData.append('list_name', listName.trim());

      const response = await fetch('https://tetrus.ai/api/import', {
        method: 'POST',
        headers: { 'X-API-Key': TETRUS_API_KEY },
        body: formData
      });
      const data = await response.json();
      setResult(data);
      setStep(5);
      loadHistory();
    } catch (err) {
      setResult({ error: err.message });
      setStep(5);
    }
    setImporting(false);
  };

  const s = {
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modal: { background: '#0D1421', border: '1px solid #1E2A3A', borderRadius: 12, padding: 32, width: 700, maxHeight: '80vh', overflowY: 'auto' },
    title: { fontSize: 18, fontWeight: 700, color: '#F1F5F9', marginBottom: 4 },
    sub: { fontSize: 12, color: '#475569', fontFamily: 'monospace', marginBottom: 24 },
    btn: { padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none', fontFamily: 'inherit' },
    btnP: { background: '#0EA5E9', color: 'white' },
    btnG: { background: 'transparent', border: '1px solid #243044', color: '#94A3B8' },
    label: { fontSize: 11, color: '#475569', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6, display: 'block' },
    select: { background: '#111827', border: '1px solid #1E2A3A', color: '#F1F5F9', padding: '6px 10px', borderRadius: 6, fontSize: 12, width: '100%' },
    issue: { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#FCA5A5', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 6 },
    warning: { background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', color: '#FCD34D', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 6 },
    success: { background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', color: '#6EE7B7', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 6 },
  };

  return (
    <div style={s.overlay}>
      <div style={s.modal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 20 }}>
          <div>
            <div style={s.title}>Import Leads</div>
            {activeTab === 'import' && (
              <div style={s.sub}>STEP {step} OF 5 — {['', 'UPLOAD FILE', 'MAP COLUMNS', 'RESOLVE AMBIGUITY', 'VALIDATE DATA', 'IMPORT COMPLETE'][step]}</div>
            )}
            {activeTab === 'history' && (
              <div style={s.sub}>IMPORT HISTORY — {history.length} PAST IMPORTS</div>
            )}
          </div>
          <button onClick={onClose} style={{ ...s.btn, ...s.btnG }}>✕ Close</button>
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #1E2A3A', marginBottom: 24 }}>
          {[['import', '⬆ New Import'], ['history', '🕐 Import History']].map(([id, label]) => (
            <div
              key={id}
              onClick={() => setActiveTab(id)}
              style={{
                padding: '8px 20px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                borderBottom: activeTab === id ? '2px solid #0EA5E9' : '2px solid transparent',
                color: activeTab === id ? '#0EA5E9' : '#475569',
                transition: 'all 0.15s',
              }}
            >{label}</div>
          ))}
        </div>

        {activeTab === 'import' && (<>

        {step === 1 && (
          <div>
            <div style={{ marginBottom: 20 }}>
              <div style={s.label}>List Name *</div>
              <input
                type="text"
                placeholder="e.g. Healthcare Q2 2026 — US"
                value={listName}
                onChange={e => setListName(e.target.value)}
                style={{
                  width: '100%', padding: '10px 12px', background: '#111827',
                  border: listName.trim() ? '1px solid #0EA5E9' : '1px solid #1E2A3A',
                  color: '#F1F5F9', borderRadius: 6, fontSize: 13, boxSizing: 'border-box',
                  fontFamily: 'inherit', outline: 'none',
                }}
              />
              <div style={{ fontSize: 11, color: '#CBD5E1', marginTop: 5 }}>
                This name tags every contact in this import so you can filter by source list later.
              </div>
            </div>
            <div style={s.label}>Upload CSV File</div>
            <div style={{
              border: listName.trim() ? '2px dashed #0EA5E9' : '2px dashed #1E2A3A',
              borderRadius: 8, padding: 40, textAlign: 'center',
              opacity: listName.trim() ? 1 : 0.5, pointerEvents: listName.trim() ? 'auto' : 'none',
              transition: 'all 0.2s',
            }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>📁</div>
              <div style={{ color: '#94A3B8', marginBottom: 16, fontSize: 14 }}>Drop your CSV file here or click to browse</div>
              <input type="file" accept=".csv" onChange={handleFile} style={{ display: 'none' }} id="csv-upload" disabled={!listName.trim()} />
              <label htmlFor="csv-upload" style={{ ...s.btn, ...s.btnP, cursor: listName.trim() ? 'pointer' : 'not-allowed' }}>Choose File</label>
            </div>
            {!listName.trim() && (
              <div style={{ marginTop: 10, fontSize: 12, color: '#F59E0B', textAlign: 'center' }}>
                ↑ Enter a list name above to enable file upload
              </div>
            )}
          </div>
        )}

        {step === 2 && !currentAmbiguity && (
          <div>
            <div style={s.label}>Map Your Columns to TETRUS Fields</div>
            <div style={{ marginBottom: 16, color: '#94A3B8', fontSize: 12 }}>File: {file?.name} — {headers.length} columns detected</div>
            {headers.map(h => (
              <div key={h} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <div style={{ width: 180, fontSize: 12, color: '#F1F5F9', fontFamily: 'monospace' }}>{h}</div>
                <div style={{ color: '#475569', fontSize: 12 }}>→</div>
                <select style={s.select} value={mapping[h] || ''} onChange={e => setMapping(prev => ({ ...prev, [h]: e.target.value }))}>
                  {TETRUS_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
              <button style={{ ...s.btn, ...s.btnP }} onClick={validateData}>Validate Data →</button>
              <button style={{ ...s.btn, ...s.btnG }} onClick={() => setStep(1)}>Back</button>
            </div>
          </div>
        )}

        {step === 2 && currentAmbiguity && (
          <div>
            <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 8, padding: 20, marginBottom: 24 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#FCD34D', marginBottom: 8 }}>⚠ Ambiguous Column Detected</div>
              <div style={{ fontSize: 13, color: '#94A3B8', marginBottom: 16 }}>Column: <span style={{ color: '#F1F5F9', fontFamily: 'monospace' }}>{currentAmbiguity.col}</span></div>
              <div style={{ fontSize: 14, color: '#F1F5F9', marginBottom: 16 }}>{currentAmbiguity.question}</div>
              <div style={{ display: 'flex', gap: 10 }}>
                {currentAmbiguity.options.map(opt => (
                  <button key={opt} style={{ ...s.btn, ...s.btnP }} onClick={() => resolveAmbiguity(currentAmbiguity.col, opt)}>
                    {TETRUS_FIELDS.find(f => f.value === opt)?.label || opt}
                  </button>
                ))}
                <button style={{ ...s.btn, ...s.btnG }} onClick={() => resolveAmbiguity(currentAmbiguity.col, '')}>Skip</button>
              </div>
            </div>
            <div style={{ color: '#475569', fontSize: 12 }}>{ambiguityQueue.length} ambiguous column{ambiguityQueue.length !== 1 ? 's' : ''} remaining</div>
          </div>
        )}

        {step === 4 && validation && (
          <div>
            <div style={s.label}>Data Quality Report</div>
            {validation.issues.length === 0 && validation.warnings.length === 0 && (
              <div style={s.success}>✓ No issues found. Data looks clean and ready to import.</div>
            )}
            {validation.issues.length > 0 && (
              <div style={{ ...s.warning, marginBottom: 8 }}>
                ⚠ {validation.issues.length} preview row{validation.issues.length !== 1 ? 's have' : ' has'} formatting issues — these rows will be <strong>skipped automatically</strong> during import. All other rows will still be imported.
              </div>
            )}
            {validation.issues.map((issue, i) => <div key={i} style={{ ...s.issue, opacity: 0.75, fontSize: 11 }}>→ {issue}</div>)}
            {validation.warnings.map((w, i) => <div key={i} style={s.warning}>⚠ {w}</div>)}
            <div style={{ marginTop: 16, padding: '12px 16px', background: '#111827', borderRadius: 8, fontSize: 12, color: '#94A3B8' }}>
              Preview: {preview.length} rows sampled from {file?.name}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
              <button style={{ ...s.btn, ...s.btnP }} onClick={handleImport} disabled={importing}>
                {importing ? 'Importing...' : 'Confirm Import →'}
              </button>
              <button style={{ ...s.btn, ...s.btnG }} onClick={() => setStep(2)}>Back to Mapping</button>
            </div>
          </div>
        )}

        {step === 5 && (
          <div>
            {result?.error ? (
              <div style={s.issue}>✕ Import failed: {result.error}</div>
            ) : (
              <>
                <div style={s.success}>✓ Import complete. Records are now in the TETRUS database and ready for cleaning.</div>
                {result?.imported != null && (
                  <div style={{ marginTop: 12, display: 'flex', gap: 16 }}>
                    <div style={{ flex: 1, background: '#111827', borderRadius: 8, padding: '12px 16px', textAlign: 'center' }}>
                      <div style={{ fontSize: 22, fontWeight: 700, color: '#10B981' }}>{(result.imported || 0).toLocaleString()}</div>
                      <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>IMPORTED</div>
                    </div>
                    <div style={{ flex: 1, background: '#111827', borderRadius: 8, padding: '12px 16px', textAlign: 'center' }}>
                      <div style={{ fontSize: 22, fontWeight: 700, color: '#F59E0B' }}>{(result.rejected || 0).toLocaleString()}</div>
                      <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>REJECTED</div>
                    </div>
                    <div style={{ flex: 1, background: '#111827', borderRadius: 8, padding: '12px 16px', textAlign: 'center' }}>
                      <div style={{ fontSize: 22, fontWeight: 700, color: '#0EA5E9' }}>
                        {result.total_rows > 0 ? Math.round((result.imported / result.total_rows) * 100) : 0}%
                      </div>
                      <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>PASS RATE</div>
                    </div>
                  </div>
                )}
              </>
            )}
                {result?.error_summary && Object.keys(result.error_summary).length > 0 && (
                  <div style={{ marginTop: 12, background: '#111827', borderRadius: 8, padding: '12px 16px' }}>
                    <div style={{ fontSize: 11, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 8 }}>Skipped Row Reasons</div>
                    {Object.entries(result.error_summary).map(([reason, count]) => (
                      <div key={reason} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#94A3B8', padding: '3px 0', borderBottom: '1px solid #1E2A3A' }}>
                        <span>{reason}</span>
                        <span style={{ color: '#F59E0B', fontWeight: 600 }}>{count}</span>
                      </div>
                    ))}
                  </div>
                )}
            {result?.skipped_rows?.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: 11, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.07em' }}>
                    Skipped Records ({result.skipped_rows.length}{result.skipped_rows.length === 500 ? '+' : ''})
                  </div>
                  <button style={{ ...s.btn, ...s.btnG, fontSize: 11, padding: '4px 10px' }} onClick={() => downloadSkippedCSV(result.skipped_rows)}>
                    ↓ Download Skipped CSV
                  </button>
                </div>
                <div style={{ overflowX: 'auto', maxHeight: 240, overflowY: 'auto', borderRadius: 6, border: '1px solid #1E2A3A' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr style={{ background: '#0D1421' }}>
                        {['Row #', 'Email', 'Reason'].map(h => (
                          <th key={h} style={{ textAlign: 'left', padding: '6px 10px', color: '#475569', borderBottom: '1px solid #1E2A3A', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: '#0D1421' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.skipped_rows.map((r, i) => {
                        let email = '';
                        try { email = JSON.parse(r.raw_data)?.email || ''; } catch {}
                        return (
                          <tr key={i} style={{ borderBottom: '1px solid #111827' }}>
                            <td style={{ padding: '5px 10px', color: '#64748B', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{r.row_number}</td>
                            <td style={{ padding: '5px 10px', color: '#94A3B8', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email || <span style={{ color: '#475569' }}>—</span>}</td>
                            <td style={{ padding: '5px 10px', color: '#FCA5A5' }}>{r.error_reason}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <button style={{ ...s.btn, ...s.btnP, marginTop: 16 }} onClick={() => { setListName(''); setStep(1); setFile(null); setResult(null); onClose(); }}>Done</button>
          </div>
        )}

        </>)}

        {activeTab === 'history' && (
          <div>
            {historyLoading ? (
              <div style={{ color: '#475569', padding: 32, textAlign: 'center' }}>Loading history...</div>
            ) : history.length === 0 ? (
              <div style={{ color: '#475569', padding: 32, textAlign: 'center', fontSize: 13 }}>
                No imports yet. Upload a CSV file to get started.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      {['File', 'Date', 'Total', 'Imported', 'Rejected', 'Pass Rate', 'Status'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: '#475569', borderBottom: '1px solid #1E2A3A', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {history.map(imp => {
                      const passRate = imp.total_rows > 0 ? Math.round((imp.imported / imp.total_rows) * 100) : 0;
                      const statusColor = imp.status === 'success' ? '#10B981' : imp.status === 'rejected' ? '#EF4444' : '#F59E0B';
                      return (
                        <tr key={imp.id} style={{ borderBottom: '1px solid #111827' }}>
                          <td style={{ padding: '9px 10px', color: '#F1F5F9', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={imp.filename}>
                            📄 {imp.filename}
                          </td>
                          <td style={{ padding: '9px 10px', color: '#94A3B8', whiteSpace: 'nowrap' }}>
                            {new Date(imp.created_at).toLocaleString()}
                          </td>
                          <td style={{ padding: '9px 10px', color: '#94A3B8', textAlign: 'right' }}>
                            {Number(imp.total_rows).toLocaleString()}
                          </td>
                          <td style={{ padding: '9px 10px', color: '#10B981', fontWeight: 600, textAlign: 'right' }}>
                            {Number(imp.imported).toLocaleString()}
                          </td>
                          <td style={{ padding: '9px 10px', color: imp.rejected > 0 ? '#F59E0B' : '#475569', textAlign: 'right' }}>
                            {Number(imp.rejected).toLocaleString()}
                          </td>
                          <td style={{ padding: '9px 10px', textAlign: 'right' }}>
                            <span style={{
                              color: passRate >= 90 ? '#10B981' : passRate >= 70 ? '#F59E0B' : '#EF4444',
                              fontWeight: 600
                            }}>{passRate}%</span>
                          </td>
                          <td style={{ padding: '9px 10px' }}>
                            <span style={{
                              background: statusColor + '18', color: statusColor,
                              padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600
                            }}>{imp.status}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{ marginTop: 16, display: 'flex', gap: 20, padding: '12px 0', borderTop: '1px solid #1E2A3A' }}>
                  <div style={{ fontSize: 12, color: '#475569' }}>
                    Total imports: <span style={{ color: '#F1F5F9', fontWeight: 600 }}>{history.length}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#475569' }}>
                    All-time imported: <span style={{ color: '#10B981', fontWeight: 600 }}>
                      {history.reduce((s, i) => s + Number(i.imported), 0).toLocaleString()}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#475569' }}>
                    Avg pass rate: <span style={{ color: '#0EA5E9', fontWeight: 600 }}>
                      {history.length > 0
                        ? Math.round(history.reduce((s, i) => s + (i.total_rows > 0 ? (i.imported / i.total_rows) * 100 : 0), 0) / history.length)
                        : 0}%
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
